import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CoverageComponentTypeValues,
  CoverageTypes,
  ICar,
  ICoverageRuleLimitSummary,
  ICoverageRuleSummary,
  IEnergyWalletBalanceDTO,
  IEnergyWalletPolicy,
  ISponsorshipActivationRuleSummary,
  SponsorshipScheduleDayValues,
  IUser,
} from 'common_library';
import { BehaviorSubject } from 'rxjs';
import { CarService } from 'src/app/services/entities/car.service';
import { MasterDetailShowType } from '../master-detail/master-detail.component';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import { MainStateService } from 'src/app/services/state/app-main-state.service';
import { EnergyWalletService } from 'src/app/services/entities/energy-wallet.service';
import { TranslateConfigService } from 'src/app/services/translate.service';
import { UserService } from 'src/app/services/entities/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ImpersonateUserModalComponent } from '../impersonate-user-modal/impersonate-user-modal.component';

export enum DetailTypes {
  User,
  Car,
  Sponsorship,
  Language,
  NotificationPreferences,
  None
}

enum DetailTitles {
  User = 'Modifica profilo',
  Car = 'Modifica veicolo',
  Sponsorship = 'Coperture',
  Language = 'Lingua',
  NotificationPreferences = 'Preferenze notifiche'
}

type ProfileSponsorshipItem = Partial<IEnergyWalletPolicy> & {
  id?: number;
  name: string;
  companyId?: number;
  companyName?: string;
  appliesToGroupId?: number;
  appliesToGroupName?: string;
  activationRules?: ISponsorshipActivationRuleSummary[];
  coverageRules?: ICoverageRuleSummary[];
  coverageRuleLimits?: ICoverageRuleLimitSummary[];
};

@Component({
    selector: 'yf-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    standalone: false
})
export class UserProfileComponent implements OnInit, OnDestroy {
  readonly DetailTypes = DetailTypes;
  readonly DetailTitles = DetailTitles;
  detailType: DetailTypes = DetailTypes.User;

  masterDetailShow: MasterDetailShowType = 'master';
  compactMode: boolean = false;
  user: IUser;
  selectedCar$: BehaviorSubject<ICar> = new BehaviorSubject(null);
  energyWallet?: IEnergyWalletBalanceDTO | null;
  sponsorships: ProfileSponsorshipItem[] = [];
  selectedSponsorship?: ProfileSponsorshipItem | null;
  titleDetail: DetailTitles;
  savingNewCar: boolean = false;
  selectedLanguage = 'en';
  readonly languageOptions = [
    { value: 'it', labelKey: 'PROFILE.LANGUAGE_ITALIAN' },
    { value: 'en', labelKey: 'PROFILE.LANGUAGE_ENGLISH' },
  ];

  constructor(
    public mainStateService: MainStateService,
    public carService: CarService,
    private translate: TranslateService,
    private energyWalletService: EnergyWalletService,
    private translateConfigService: TranslateConfigService,
    private userService: UserService,
    private authService: AuthService,
    private modalCtrl: ModalController,
  ) { }

  get isAdmin(): boolean {
    return !!this.mainStateService.getUser()?.isAdmin;
  }

  /** Apre il picker utenti e, se scelto, avvia l'impersonation (assistenza). */
  async openImpersonate(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: ImpersonateUserModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role !== 'confirm' || !data?.id) return;
    const state = await this.authService.impersonate(data.id);
    if (state) await this.authService.enterImpersonation(state);
  }

  openNotificationPreferences() {
    this.showDetail(DetailTypes.NotificationPreferences, DetailTitles.NotificationPreferences);
  }

  get newVehicle(): ICar | null {
    return this.carService.newVehicle || null;
  }

  set newVehicle(car: ICar | null) {
    this.carService.newVehicle = car;
  }

  async ngOnInit() {
    if (!!this.newVehicle) {
      this.savingNewCar = true;
      this.showDetail(DetailTypes.Car, DetailTitles.Car, null);
    }

    this.selectedLanguage = this.translateConfigService.getDefaultLanguage();
    await this.loadSponsorships();
  }

  ngOnDestroy(): void {
    if (!this.newVehicle?.model) {
      this.newVehicle = null;
      this.savingNewCar = false;
    }
  }

  compactModeChange(compactMode: boolean) {
    this.compactMode = compactMode;
  }

  showDetail(detailType: DetailTypes, titleType: DetailTitles, car?: ICar, sponsorship?: ProfileSponsorshipItem) {

    if (car?.id && detailType === DetailTypes.Car) {
      this.selectedCar$.next(car);
    } else if (detailType === DetailTypes.Car && this.savingNewCar) {
      this.selectedCar$.next(null)
    } else if (detailType === DetailTypes.Car && !car) {
      this.selectedCar$.next(null)
      this.savingNewCar = true;
    }

    if (detailType === DetailTypes.Sponsorship) {
      this.selectedSponsorship = sponsorship || this.sponsorships[0] || null;
    }

    this.detailType = detailType;
    this.masterDetailShow = 'detail';
    this.setDetailTitle(titleType);
  }

  handleCancelEvent(car?: ICar) {
    if (!car) {
      this.selectedCar$.next(null);
    } else {
      this.selectedCar$.next(car || this.user.cars[0])
    }
    this.savingNewCar = false;
    this.newVehicle = null;
  }
  handleDeleteEvent() {
    this.masterDetailShow = 'master';
    this.selectedCar$.next(this.user.cars[0])
  }

  handleFormChanges(form: ICar) {
    this.newVehicle = form;
    this.selectedCar$.next(form)
  }

  selectCar(carId: number) {
    this.carService.selectCar(carId);
  }

  async changeLanguage(language: string) {
    if (!this.translateConfigService.isLanguageAllowed(language)) return;
    this.selectedLanguage = language;
    this.translateConfigService.setLanguage(language);
    const user = this.mainStateService.getUser();
    if (user?.lang?.frontend !== language) {
      const updatedUser = await this.userService.updateMe({ lang: { frontend: language } });
      if (updatedUser) this.mainStateService.setUser(updatedUser);
    }
    this.setDetailTitle(DetailTitles.Language);
  }

  async loadSponsorships() {
    try {
      this.energyWallet = await this.energyWalletService.getMine();
      this.sponsorships = this.buildSponsorshipItems(this.energyWallet);
      if (!this.sponsorships.length && this.detailType === DetailTypes.Sponsorship) {
        this.showDetail(DetailTypes.User, DetailTitles.User);
      }
    } catch (error) {
      this.energyWallet = null;
      this.sponsorships = [];
      if (this.detailType === DetailTypes.Sponsorship) {
        this.showDetail(DetailTypes.User, DetailTitles.User);
      }
    }
  }

  private buildSponsorshipItems(wallet?: IEnergyWalletBalanceDTO | null): ProfileSponsorshipItem[] {
    if (!wallet) return [];

    if (wallet.sponsorship?.policyName && wallet.sponsorship.appliesToCurrentUser) {
      return [{
        id: wallet.sponsorship.policyId,
        name: wallet.sponsorship.policyName,
        companyId: wallet.sponsorship.companyId,
        companyName: wallet.sponsorship.companyName,
        appliesToGroupId: wallet.sponsorship.appliesToGroupId,
        appliesToGroupName: wallet.sponsorship.appliesToGroupName,
        activationRules: wallet.sponsorship.activationRules || [],
        coverageRules: wallet.sponsorship.coverageRules || [],
        coverageRuleLimits: wallet.sponsorship.coverageRuleLimits || [],
      }];
    }

    return [];
  }

  formatPolicyDate(value?: Date | string): string {
    if (!value) return '-';
    return new Date(value).toLocaleDateString();
  }

  hasCoverageRules(sponsorship?: ProfileSponsorshipItem | null): boolean {
    return !!sponsorship?.coverageRules?.length;
  }

  hasActivationRules(sponsorship?: ProfileSponsorshipItem | null): boolean {
    return !!sponsorship?.activationRules?.length;
  }

  activationRuleText(rule: ISponsorshipActivationRuleSummary): string {
    const startDay = this.scheduleDayLabel(rule?.startDay);
    const endDay = this.scheduleDayLabel(rule?.endDay);
    const startTime = this.formatRuleTime(rule?.startTime);
    const endTime = this.formatRuleTime(rule?.endTime);

    if (startDay && endDay && startTime && endTime) {
      return this.translate.instant('PROFILE.SPONSORSHIP_TIME_WINDOW_RANGE', { startDay, startTime, endDay, endTime });
    }
    if (startDay && startTime && endTime) {
      return this.translate.instant('PROFILE.SPONSORSHIP_TIME_WINDOW_SAME_DAY', { day: startDay, startTime, endTime });
    }
    if (startTime && endTime) {
      return this.translate.instant('PROFILE.SPONSORSHIP_TIME_WINDOW_DAILY', { startTime, endTime });
    }
    return rule?.notes || this.translate.instant('PROFILE.SPONSORSHIP_TIME_WINDOW_CONFIGURED');
  }

  activationRuleTimezone(rule: ISponsorshipActivationRuleSummary): string {
    return rule?.timezone || '';
  }

  coverageRuleText(rule: ICoverageRuleSummary): string {
    const component = this.coverageComponentLabel(rule?.componentType);
    const type = String(rule?.coverageType || '').toUpperCase();
    const value = Number(rule?.value || 0);
    const unit = rule?.unit || '';

    if (type === CoverageTypes.FULL) {
      return this.translate.instant('PROFILE.SPONSORSHIP_COVERAGE_FULL', { component });
    }
    if (type === CoverageTypes.PERCENT) {
      return this.translate.instant('PROFILE.SPONSORSHIP_COVERAGE_PERCENT', { component, value });
    }
    if (type === CoverageTypes.FIXED_DISCOUNT) {
      return this.translate.instant('PROFILE.SPONSORSHIP_COVERAGE_FIXED', { component, value, unit });
    }
    return this.translate.instant('PROFILE.SPONSORSHIP_COVERAGE_NONE', { component });
  }

  private coverageComponentLabel(componentType?: CoverageComponentTypeValues | string | null): string {
    const key = String(componentType || 'GENERIC').toUpperCase();
    return this.translate.instant(`PROFILE.SPONSORSHIP_COMPONENT.${key}`);
  }

  private scheduleDayLabel(day?: SponsorshipScheduleDayValues | string | null): string {
    if (!day) return '';
    const key = String(day).toUpperCase();
    return this.translate.instant(`PROFILE.SPONSORSHIP_DAY.${key}`);
  }

  private formatRuleTime(time?: string): string {
    if (!time) return '';
    return String(time).slice(0, 5);
  }

  setDetailTitle(titleType: DetailTitles) {
    let title: string;
    if (titleType === DetailTitles.User) {
      title = 'PROFILE.USER_DETAIL.EDIT';
    } else if (titleType === DetailTitles.Car) {
      title = 'CAR.CAR_DETAIL.EDIT';
    } else if (titleType === DetailTitles.Language) {
      title = 'PROFILE.LANGUAGE';
    } else if (titleType === DetailTitles.NotificationPreferences) {
      title = 'NOTIF_PREFS.TITLE';
    } else {
      title = 'PROFILE.SPONSORSHIPS';
    }
    this.translate.get(title).subscribe((res: string) => {
      this.titleDetail = res as DetailTitles;
    });
  }
}
